import { IComponentOptions, IScope } from "angular";
import { GtmhubController } from "@gtmhub/core";
import { EmojiButton, EmojiSelection } from "@joeattardi/emoji-button";
import { IActiveReaction, IReactionSettings } from "../models";
import { WhiteboardEvents } from "../events";
import { WhiteboardsService } from "../whiteboards-service";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { IWhiteboardStateParams } from "./whiteboard-menu.component";
import { MaxReactionsPerUserAllowed } from "../rendering/behaviors/voting";

export interface IWhiteboardReactionSettingsBindings {
  reactionSettings: IReactionSettings;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface WhiteboardReactionSettingsComponentCtrl extends IWhiteboardReactionSettingsBindings {}

export class WhiteboardReactionSettingsComponentCtrl extends GtmhubController {
  private emojiButton: EmojiButton;
  private events: WhiteboardEvents;
  private whiteboardId: string;
  private areReactionsActive: boolean;
  isOneReactionPerItemEnabled: boolean;
  isEmojiPickerOpen: boolean;
  isReactionDragged: boolean;

  static $inject = ["WhiteboardsService", "$scope", "UIErrorHandlingService", "$stateParams"];

  get isLastReaction(): boolean {
    return this.reactionSettings.activeReactions.length === 1;
  }

  constructor(
    private whiteboardsService: WhiteboardsService,
    $scope: IScope,
    private uiErrorHandlingService: UIErrorHandlingService,
    private $stateParams: IWhiteboardStateParams
  ) {
    super();

    this.whiteboardId = this.$stateParams.whiteboardId;
    this.events = new WhiteboardEvents($scope);

    const htmlElement = document.getElementById("reaction-settings");
    this.emojiButton = new EmojiButton({ position: "bottom-start", showVariants: false, showAnimation: false, showPreview: false, rootElement: htmlElement });
    this.isEmojiPickerOpen = false;
    this.isReactionDragged = false;

    this.setWhiteboardEvents();
    this.onHideEmojiPicker();
    this.onSelectEmoji();
  }

  $onInit(): void {
    this.areReactionsActive = this.reactionSettings.isActive;
    this.isOneReactionPerItemEnabled = this.reactionSettings.isOneReactionPerItem;
  }

  onReactionsActiveToggle(): void {
    this.reactionSettings.isActive = !this.reactionSettings.isActive;

    this.onChangeSettings();
  }

  openEmojiPicker(): void {
    const htmlElement = document.getElementById("reaction-settings");

    this.emojiButton.showPicker(htmlElement);
    this.isEmojiPickerOpen = true;
  }

  onHideEmojiPicker(): void {
    this.emojiButton.on("hidden", () => {
      this.isEmojiPickerOpen = false;
    });
  }

  onSelectEmoji(): void {
    this.emojiButton.on("emoji", (selectedEmoji: EmojiSelection) => {
      const newEmoji: IActiveReaction = {
        name: selectedEmoji.name.replace(/\s+/g, "-"),
        emoji: selectedEmoji.emoji,
      };

      this.addActiveReaction(newEmoji);
    });
  }

  addActiveReaction(reaction: IActiveReaction): void {
    if (!this.reactionSettings.activeReactions.map((r) => r.name).includes(reaction.name)) {
      this.reactionSettings.activeReactions.push(reaction);
    }

    this.isEmojiPickerOpen = false;
    this.onChangeSettings();
  }

  onDeleteEmoji(index: number): void {
    this.reactionSettings.activeReactions.splice(index, 1);

    this.onChangeSettings();
  }

  onChangeSettings(): void {
    this.whiteboardsService
      .update(this.whiteboardId, { reactionSettings: this.reactionSettings })
      .then((response) => {
        // only broadcast event if there was a change to reactionSettings.isActive
        if (response.reactionSettings.isActive && !this.areReactionsActive) {
          this.events.broadcastReactionsEnabled();
        }
        if (!response.reactionSettings.isActive && this.areReactionsActive) {
          this.events.broadcastReactionsDisabled();
        }

        const haveIsOneReactionPerItemSettingsChanged = response.reactionSettings.isOneReactionPerItem !== this.isOneReactionPerItemEnabled;
        if (haveIsOneReactionPerItemSettingsChanged) {
          this.events.broadcastMaxReactionsPerUserAllowedChanged(
            response.reactionSettings.isOneReactionPerItem ? MaxReactionsPerUserAllowed.RESTRICTED : MaxReactionsPerUserAllowed.DEFAULT
          );
          this.isOneReactionPerItemEnabled = response.reactionSettings.isOneReactionPerItem;
        }
        this.areReactionsActive = response.reactionSettings.isActive;
      })
      .catch((rejection) => {
        this.uiErrorHandlingService.handleModal(rejection);
      });
  }

  private setWhiteboardEvents(): void {
    this.events.onReactionSettingsChanged((args) => {
      const isChangeTriggeredByCollaborator =
        this.isOneReactionPerItemEnabled === this.reactionSettings.isOneReactionPerItem && this.isOneReactionPerItemEnabled !== args.payload.isOneReactionPerItem;
      if (isChangeTriggeredByCollaborator) {
        this.isOneReactionPerItemEnabled = args.payload.isOneReactionPerItem;
      }
    });
  }
}

export const WhiteboardReactionSettingsComponent: IComponentOptions = {
  templateUrl: require("./whiteboard-reaction-settings.jade"),
  controller: WhiteboardReactionSettingsComponentCtrl,
  bindings: {
    reactionSettings: "<",
  },
};
